// src/apolloClient.js
import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { getAuthToken, clearAuthState } from "./utils/authUtils";

const GRAPHQL_ENDPOINT = process.env.REACT_APP_APPSYNC_ENDPOINT || "";

const httpLink = createHttpLink({
  uri: GRAPHQL_ENDPOINT,
});

// Injects Cognito JWT into every AppSync request.
// Public reads (gallery, profile) go through the Lambda Function URL instead — no Apollo.
const authLink = setContext((_, { headers }) => {
  const token = getAuthToken();
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: token } : {}),
    },
  };
});

// Handles 401/UnauthorizedException — clears auth state and redirects to login.
// Only redirects if the user had a token (expired/invalid session); guests on
// public pages silently receive an auth error handled by the saga.
const errorLink = onError(({ graphQLErrors, networkError }) => {
  const hadToken = !!localStorage.getItem("jwtToken");

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions }) => {
      if (
        extensions?.errorType === "UnauthorizedException" ||
        message.includes("Unauthorized")
      ) {
        console.error("apolloClient - Unauthorized, clearing auth state");
        clearAuthState();
        if (hadToken && window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    });
  }

  if (networkError?.statusCode === 401) {
    console.error("apolloClient - 401 network error, clearing auth state");
    clearAuthState();
    if (hadToken && window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }
});

export const client = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache(),
});
